/* __next_internal_client_entry_do_not_use__ PortalDropdown auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Popover, PopoverContent } from "@faire/web--source/slate/utils/Popover";
import ClickOutside from "@faire/web--source/ui/ClickOutside";
import * as React from "react";
import { ClearButton } from "./__internal__/ClearButton";
import { DropdownContent } from "./__internal__/DropdownContent";
import { DropdownInput } from "./__internal__/DropdownInput";
import { MobileDropdown } from "./__internal__/MobileDropdown";
import { useDropdownInteractions } from "./__internal__/useDropdownInteractions";
import { useDropdownState } from "./__internal__/useDropdownState";
import { isCustomOption } from "./__internal__/utils";
import { ClickOutsideArea, DROPDOWN_LIST_HEIGHT } from "./constants";
import { Wrapper } from "./styles";
export var PortalDropdown = function(props) {
    var options = props.options, filter = props.filter, onSelect = props.onSelect, value = props.value, containingElement = props.containingElement, disableInput = props.disableInput, className = props.className, placeholder = props.placeholder, label = props.label, error = props.error, helper = props.helper, createNewOption = props.createNewOption, disabled = props.disabled, _props_autoComplete = props.autoComplete, autoComplete = _props_autoComplete === void 0 ? "off" : _props_autoComplete, id = props.id, inputRef = props.inputRef, borderless = props.borderless, customOption = props.customOption, ariaLabel = props["aria-label"], dataTestId = props["data-test-id"], dataHjAllow = props["data-hj-allow"], useTabletAndAboveDropdownStyle = props.useTabletAndAboveDropdownStyle, onDropdownOpen = props.onDropdownOpen, onDropdownClose = props.onDropdownClose, onClear = props.onClear, customOptionMaxLength = props.customOptionMaxLength, disableOverflow = props.disableOverflow, disableFlip = props.disableFlip, zIndex = props.zIndex, isModal = props.isModal;
    var _useDropdownState = useDropdownState({
        id: id,
        inputRef: inputRef,
        filter: filter,
        options: options,
        customOption: customOption,
        createNewOption: createNewOption,
        ariaLabel: ariaLabel,
        label: label,
        error: error,
        useTabletAndAboveDropdownStyle: useTabletAndAboveDropdownStyle
    }), dropdownId = _useDropdownState.dropdownId, targetRef = _useDropdownState.targetRef, refs = _useDropdownState.refs, open = _useDropdownState.open, setOpen = _useDropdownState.setOpen, searchValue = _useDropdownState.searchValue, setSearchValue = _useDropdownState.setSearchValue, highlightIndex = _useDropdownState.highlightIndex, setHighlightIndex = _useDropdownState.setHighlightIndex, isCustomizingOption = _useDropdownState.isCustomizingOption, setIsCustomizingOption = _useDropdownState.setIsCustomizingOption, filteredOptions = _useDropdownState.filteredOptions, ariaProps = _useDropdownState.ariaProps, showErrorState = _useDropdownState.showErrorState, showErrorMessage = _useDropdownState.showErrorMessage, errorMessage = _useDropdownState.errorMessage, showMobileDropdown = _useDropdownState.showMobileDropdown;
    var _useDropdownInteractions = useDropdownInteractions({
        createNewOption: createNewOption,
        customOption: customOption,
        filteredOptions: filteredOptions,
        highlightIndex: highlightIndex,
        isCustomizingOption: isCustomizingOption,
        onDropdownClose: onDropdownClose,
        onDropdownOpen: onDropdownOpen,
        onSelect: onSelect,
        open: open,
        options: options,
        refs: refs,
        searchValue: searchValue,
        setHighlightIndex: setHighlightIndex,
        setIsCustomizingOption: setIsCustomizingOption,
        setOpen: setOpen,
        setSearchValue: setSearchValue,
        value: value,
        targetRef: targetRef
    }), closeDropdown = _useDropdownInteractions.closeDropdown, handleClickOutside = _useDropdownInteractions.handleClickOutside, handleInputClick = _useDropdownInteractions.handleInputClick, handleInputKeyDown = _useDropdownInteractions.handleInputKeyDown, handleKeyDown = _useDropdownInteractions.handleKeyDown, handleValueSelect = _useDropdownInteractions.handleValueSelect, handleOptionSelect = _useDropdownInteractions.handleOptionSelect, handleInputSearch = _useDropdownInteractions.handleInputSearch, handleCustomOptionEdit = _useDropdownInteractions.handleCustomOptionEdit, resetSearchValue = _useDropdownInteractions.resetSearchValue;
    var isSSR = useIsSSR();
    var isPortal = !isSSR && !disableOverflow;
    var inputBoxRef = React.useRef(null);
    if (showMobileDropdown) {
        return /*#__PURE__*/ _jsx(MobileDropdown, {
            borderless: borderless,
            className: className,
            dataTestId: dataTestId,
            disabled: disabled,
            errorMessage: errorMessage,
            options: options,
            helper: helper,
            id: dropdownId,
            label: label,
            onOpen: onDropdownOpen,
            onSelect: handleValueSelect,
            placeholder: placeholder,
            showErrorMessage: showErrorMessage,
            showErrorState: showErrorState,
            value: value
        });
    }
    var _inputBoxRef_current;
    return /*#__PURE__*/ _jsxs(Wrapper, _object_spread_props(_object_spread({
        "data-test-id": "dropdown-".concat(label || "default"),
        className: className,
        $disabled: disabled,
        $disableInput: disableInput
    }, ariaProps), {
        children: [
            /*#__PURE__*/ _jsx(ClickOutside, {
                onClickOutside: function() {
                    return handleClickOutside(ClickOutsideArea.INPUT);
                },
                children: /*#__PURE__*/ _jsxs(Popover, {
                    open: open,
                    containingElement: containingElement,
                    height: DROPDOWN_LIST_HEIGHT,
                    disableOverflow: disableOverflow,
                    onClickOutside: function() {
                        return handleClickOutside(ClickOutsideArea.MENU);
                    },
                    anchorRef: (_inputBoxRef_current = inputBoxRef.current) !== null && _inputBoxRef_current !== void 0 ? _inputBoxRef_current : undefined,
                    zIndex: zIndex,
                    onKeyDown: handleKeyDown,
                    isModal: isModal,
                    disableFlip: disableFlip,
                    children: [
                        /*#__PURE__*/ _jsx(DropdownInput, {
                            id: dropdownId,
                            dataTestId: dataTestId,
                            disabled: disabled,
                            label: label,
                            helper: helper,
                            error: error,
                            placeholder: placeholder,
                            disableSearch: disableInput,
                            onKeyDown: handleInputKeyDown,
                            targetRef: targetRef,
                            borderless: borderless,
                            autoComplete: autoComplete,
                            ariaLabel: ariaLabel,
                            value: value,
                            dataHjAllow: dataHjAllow,
                            onClear: onClear,
                            maxLength: customOptionMaxLength,
                            onClick: handleInputClick,
                            options: filteredOptions,
                            searchValue: searchValue,
                            onSearch: handleInputSearch,
                            inputBoxRef: inputBoxRef
                        }),
                        /*#__PURE__*/ _jsx(PopoverContent, {
                            children: /*#__PURE__*/ _jsx(DropdownContent, {
                                createNewOption: createNewOption,
                                searchValue: searchValue,
                                closeDropdown: closeDropdown,
                                resetSearchValue: resetSearchValue,
                                showErrorMessage: showErrorMessage,
                                helper: helper,
                                open: open,
                                filteredOptions: filteredOptions,
                                isCustomOption: isCustomOption,
                                onSelect: handleOptionSelect,
                                highlightIndex: highlightIndex,
                                onHighlight: setHighlightIndex,
                                refs: refs,
                                targetRef: targetRef,
                                containingElement: containingElement,
                                options: options,
                                onCustomOptionEdit: handleCustomOptionEdit,
                                isPortal: isPortal
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(ClearButton, {
                value: value,
                onClear: onClear,
                helper: helper
            })
        ]
    }));
};
PortalDropdown.displayName = "PortalDropdown";
